import React, { useState, useEffect, useCallback } from 'react';
import styles from '../../pages/dashboard/DashboardUI.module.css';
import settingsStyles from '../../pages/dashboard/Settings.module.css';
import {
  getLeadActions,
  createLeadAction,
  updateLeadAction,
  deleteLeadAction,
  reorderLeadActions,
} from '../../services/leadService';
import ConfirmDialog from '../ConfirmDialog';
import ReorderListModal from '../ReorderListModal';

const COLOR_PALETTE = [
  '#3b82f6', '#10b981', '#f59e0b', '#8b5cf6',
  '#ec4899', '#06b6d4', '#f97316', '#84cc16',
  '#6366f1', '#14b8a6', '#ef4444', '#6b7280'
];

const COLOR_NAMES = [
  'Blue', 'Emerald', 'Amber', 'Violet',
  'Pink', 'Cyan', 'Orange', 'Lime',
  'Indigo', 'Teal', 'Red', 'Gray'
];

function getTextColor(bgColor) {
  if (!bgColor) return '#ffffff';
  const hex = bgColor.replace('#', '');
  const r = parseInt(hex.substring(0, 2), 16);
  const g = parseInt(hex.substring(2, 4), 16);
  const b = parseInt(hex.substring(4, 6), 16);
  const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255;
  return luminance > 0.5 ? '#1f2937' : '#ffffff';
}

export default function LeadActionManager({ onMessage }) {
  const [actions, setActions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingAction, setEditingAction] = useState(null);
  const [deleting, setDeleting] = useState(null);
  const [confirmDelete, setConfirmDelete] = useState(null);
  const [showReorderModal, setShowReorderModal] = useState(false);
  const [savingOrder, setSavingOrder] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    color: '#3b82f6',
    description: ''
  });

  const loadActions = useCallback(async () => {
    try {
      setLoading(true);
      const data = await getLeadActions();
      setActions(data.actions || []);
    } catch (error) {
      onMessage({ message: error.message || 'Failed to load lead actions', type: 'error' });
    } finally {
      setLoading(false);
    }
  }, [onMessage]);

  useEffect(() => {
    loadActions();
  }, [loadActions]);

  const openCreateModal = () => {
    setEditingAction(null);
    setFormData({ name: '', color: '#3b82f6', description: '' });
    setShowModal(true);
  };

  const openEditModal = (action) => {
    setEditingAction(action);
    setFormData({
      name: action.name,
      color: action.color || '#6b7280',
      description: action.description || ''
    });
    setShowModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
    setEditingAction(null);
    setFormData({ name: '', color: '#3b82f6', description: '' });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.name.trim()) {
      onMessage({ message: 'Action name is required', type: 'error' });
      return;
    }

    try {
      if (editingAction) {
        await updateLeadAction(editingAction.action_id, {
          name: formData.name.trim(),
          color: formData.color,
          description: formData.description.trim()
        });
        onMessage({ message: 'Action updated successfully', type: 'success' });
      } else {
        await createLeadAction({
          name: formData.name.trim(),
          color: formData.color,
          description: formData.description.trim()
        });
        onMessage({ message: 'Action created successfully', type: 'success' });
      }
      closeModal();
      await loadActions();
    } catch (error) {
      const msg = error?.response?.data?.error || error.message || 'Failed to save action';
      onMessage({ message: msg, type: 'error' });
    }
  };

  const handleDelete = async (action) => {
    if (deleting === action.action_id) return;
    setDeleting(action.action_id);
    try {
      await deleteLeadAction(action.action_id);
      onMessage({ message: `Action "${action.name}" deleted`, type: 'success' });
      await loadActions();
    } catch (error) {
      const msg = error?.response?.data?.error || error.message || 'Failed to delete action';
      onMessage({ message: msg, type: 'error' });
    } finally {
      setDeleting(null);
      setConfirmDelete(null);
    }
  };

  const handleSaveReorder = async (orderedActionIds) => {
    try {
      setSavingOrder(true);
      const response = await reorderLeadActions(orderedActionIds);
      setActions(response.actions || []);
      setShowReorderModal(false);
      onMessage({ message: 'Lead action order updated', type: 'success' });
    } catch (error) {
      const msg = error?.response?.data?.error || error.message || 'Failed to reorder actions';
      onMessage({ message: msg, type: 'error' });
    } finally {
      setSavingOrder(false);
    }
  };

  if (loading) {
    return <div className={settingsStyles.loading}>Loading lead actions...</div>;
  }

  return (
    <div>
      <div className={settingsStyles.sectionHeader}>
        <div>
          <h3 style={{ margin: 0, fontSize: '1.1rem', fontWeight: 600, color: '#1e293b' }}>
            Lead Actions
          </h3>
          <p className={settingsStyles.cardDesc} style={{ margin: '4px 0 0 0' }}>
            Manage actions that can be assigned to leads for tracking next steps.
          </p>
        </div>
        <div style={{ display: 'flex', gap: '8px' }}>
          {actions.length > 1 && (
            <button className={styles.buttonSecondary} onClick={() => setShowReorderModal(true)}>
              Reorder
            </button>
          )}
          <button className={styles.buttonPrimary} onClick={openCreateModal}>
            + New Action
          </button>
        </div>
      </div>

      {actions.length === 0 ? (
        <div className={settingsStyles.emptyState}>
          <p>No lead actions found.</p>
        </div>
      ) : (
        <div className={settingsStyles.labelsList}>
          {actions.map((action) => (
            <div key={action.action_id} className={settingsStyles.labelCard}>
              <div className={settingsStyles.labelInfo}>
                <span
                  className={settingsStyles.labelBadge}
                  style={{
                    backgroundColor: action.color || '#6b7280',
                    color: getTextColor(action.color)
                  }}
                >
                  {action.name}
                </span>
                {action.is_default && (
                  <span style={{ fontSize: '0.75rem', color: '#9ca3af', fontStyle: 'italic' }}>
                    Default
                  </span>
                )}
                {action.description && (
                  <span className={settingsStyles.labelDescription}>
                    {action.description}
                  </span>
                )}
              </div>
              <div className={settingsStyles.roleActions}>
                <button
                  className={styles.buttonSecondary}
                  onClick={() => openEditModal(action)}
                >
                  Edit
                </button>
                <button
                  className={styles.buttonDanger}
                  onClick={() => setConfirmDelete(action)}
                  disabled={deleting === action.action_id}
                >
                  {deleting === action.action_id ? 'Deleting...' : 'Delete'}
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {showModal && (
        <div className={settingsStyles.modalOverlay} onClick={closeModal}>
          <div className={settingsStyles.modal} onClick={(e) => e.stopPropagation()} style={{ minHeight: 'auto' }}>
            <div className={settingsStyles.modalHeader}>
              <h3>{editingAction ? 'Edit Action' : 'Create Action'}</h3>
              <button className={settingsStyles.closeButton} onClick={closeModal}>
                ×
              </button>
            </div>
            <div className={settingsStyles.modalContent}>
              <form onSubmit={handleSubmit}>
                <div className={settingsStyles.formGroup}>
                  <label>Name</label>
                  <input
                    type="text"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    placeholder="e.g. Site Visit, Send Quote, Follow Up"
                    autoFocus
                  />
                </div>

                <div className={settingsStyles.formGroup}>
                  <label>Color</label>
                  <div className={settingsStyles.colorPicker}>
                    {COLOR_PALETTE.map((color, index) => (
                      <button
                        key={color}
                        type="button"
                        className={`${settingsStyles.colorSwatch} ${formData.color === color ? settingsStyles.colorSwatchActive : ''}`}
                        style={{ backgroundColor: color }}
                        onClick={() => setFormData({ ...formData, color })}
                        title={COLOR_NAMES[index]}
                        aria-label={COLOR_NAMES[index]}
                      />
                    ))}
                  </div>
                </div>

                <div className={settingsStyles.formGroup}>
                  <label>Preview</label>
                  <div>
                    <span
                      className={settingsStyles.labelBadge}
                      style={{
                        backgroundColor: formData.color,
                        color: getTextColor(formData.color)
                      }}
                    >
                      {formData.name || 'Action Preview'}
                    </span>
                  </div>
                </div>

                <div className={settingsStyles.formGroup}>
                  <label>Description (optional)</label>
                  <textarea
                    value={formData.description}
                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                    placeholder="What does this action involve?"
                    rows={2}
                  />
                </div>

                <div className={settingsStyles.modalActions}>
                  <button className={styles.buttonSecondary} onClick={closeModal} type="button">
                    Cancel
                  </button>
                  <button className={styles.buttonPrimary} type="submit">
                    {editingAction ? 'Save Changes' : 'Create Action'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {confirmDelete && (
        <ConfirmDialog
          message={`Are you sure you want to delete the "${confirmDelete.name}" action? Leads using this action will have their action cleared.`}
          confirmText="Delete"
          cancelText="Cancel"
          onConfirm={() => handleDelete(confirmDelete)}
          onCancel={() => setConfirmDelete(null)}
        />
      )}

      <ReorderListModal
        isOpen={showReorderModal}
        title="Reorder Lead Actions"
        description="Drag actions to change how they appear across leads pages and filters."
        items={actions}
        getId={(item) => item.action_id}
        getLabel={(item) => item.name}
        getColor={(item) => item.color}
        onClose={() => setShowReorderModal(false)}
        onSave={handleSaveReorder}
        saving={savingOrder}
      />
    </div>
  );
}
